import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const EMPTY = { name: "", number: "", exp: "", cvv: "", address1: "", city: "", state: "", zip: "", country: "" };
const FIELDS = [["name", "Cardholder name", "cc-name"], ["number", "Card number", "cc-number"], ["exp", "Expiry (MM/YY)", "cc-exp"], ["cvv", "CVV", "cc-csc"],
  ["address1", "Billing address", "address-line1"], ["city", "City", "address-level2"], ["state", "State/Island", "address-level1"], ["zip", "ZIP/Postal code", "postal-code"], ["country", "Country (2 letters)", "country"]];
const yn = (v) => (v === true ? "yes" : v === false ? "NO" : "unknown");

function Precheck({ p }) {
  if (!p) return <p className="text-xs text-slate-600">No pre-check run yet.</p>;
  const c = p.credentials;
  return (
    <div className="text-xs" data-testid="live-test-precheck">
      <p className="font-bold" data-testid="live-test-precheck-outcome">Pre-check: {p.ok ? "PASS" : "FAIL"} <span className="font-normal">({p.at.slice(0, 16).replace("T", " ")} UTC)</span></p>
      <p>Credentials: username {c.merchant_username_length} chars · password {c.merchant_password_length} chars · endpoint approved {yn(c.endpoint_approved)}</p>
      <p>Reachable {yn(p.reachable)} · Credentials accepted {yn(p.credentials_accepted)} · Server IP registered {yn(p.ip_registered)} · Live account (no test flag) {yn(p.live_account)}</p>
      <p data-testid="live-test-server-ip">Server outbound IP: <b>{p.server_ip || "inconclusive"}</b></p>
      {p.ip_registered === false && <p className="font-bold text-burgundy" data-testid="live-test-ip-stop">IP not registered. Register {p.server_ip || "the server IP"} with the gateway, then re-run the pre-check.</p>}
      {Object.keys(p.test_flags || {}).length > 0 && <p className="font-bold text-burgundy">Test indicators: {JSON.stringify(p.test_flags)}</p>}
      <p>Currency: <b>{p.currency.toUpperCase()}</b>{p.message && <> · Gateway message: {p.message}</>}</p>
      {p.currency_warning && <p className="font-bold text-amber-800" data-testid="live-test-currency-warning">{p.currency_warning}</p>}
    </div>
  );
}

function Result({ r, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!r) return null;
  const rows = [["our_reference", "Our reference"], ["gateway_order_id", "Gateway order ID"], ["status", "Status"], ["final_status", "Final status"], ["auth_code", "Auth code"], ["resp_code", "Response code"],
    ["amount", "Amount"], ["transaction_currency", "Transaction currency"], ["card_last4", "Card (last 4)"], ["message", "Message"], ["at", "Time (UTC)"]];
  const approved = ["approved", "approved_reconciled"].includes(r.status);
  const revalidate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/integrations/gateway/live-test/${r.order_id}/revalidate`); toast[data.outcome === "PASS" ? "success" : "error"](`Re-validated (no charge): ${data.outcome}`); onChange(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="text-xs" data-testid="live-test-result">
      <p className="font-bold" data-testid="live-test-outcome">Last live test: {r.status === "approved_reconciled" ? "Approved – reconciled" : r.outcome || r.status}</p>
      <dl className="grid grid-cols-[9rem_1fr] gap-x-2">{rows.map(([k, l]) => <Fragment key={k}><dt className="text-slate-600">{l}</dt><dd>{String(r[k] ?? (k === "our_reference" ? r.order_id : "–"))}</dd></Fragment>)}</dl>
      {r.currency_fields && <p className="mt-1">Currency fields from the gateway: {Object.entries(r.currency_fields).map(([k, v]) => `${k}=${v}`).join(" · ") || "none"} · saved setting {r.currency?.toUpperCase()}</p>}
      {r.checks && <p className="mt-1">Checks: {Object.entries(r.checks).map(([k, v]) => `${k.replace(/_/g, " ")} ${v ? "✓" : "✗"}`).join(" · ")}</p>}
      {(r.notes || []).map((n) => <p key={n} className="mt-1 font-semibold text-amber-900" data-testid="live-test-note">{n}</p>)}
      {r.response && <details className="mt-1"><summary>Sanitised gateway response</summary><pre className="whitespace-pre-wrap">{JSON.stringify(r.response, null, 1)}</pre></details>}
      {r.response && r.status !== "approved_reconciled" && <button type="button" className="btn-outline btn-sm mt-2" disabled={busy} onClick={revalidate} data-testid="live-test-revalidate-btn">{busy ? "Checking…" : "Re-validate stored response (no charge)"}</button>}
      {approved && <LiveReceipt orderId={r.order_id} />}
    </div>
  );
}

export function LiveReceipt({ orderId }) {
  const download = async () => {
    try {
      const r = await api.get(`/admin/integrations/gateway/live-test/${orderId}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `receipt-${orderId}.pdf` });
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <button type="button" className="btn-primary btn-sm ml-2 mt-2" onClick={download} data-testid={`live-test-receipt-btn-${orderId}`}>Download receipt (PDF)</button>;
}

export function GatewayLiveTest() {
  const [s, setS] = useState(null);
  const [card, setCard] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const load = () => api.get("/admin/integrations/gateway/live-test").then((r) => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const pre = async () => {
    setBusy(true);
    try { setS({ ...s, ...(await api.post("/admin/integrations/gateway/live-test/precheck")).data }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const charge = async (e) => {
    e.preventDefault();
    if (inflight.current || !window.confirm("This will charge $1.00 to the card entered. Continue?")) return;
    inflight.current = true; setBusy(true);
    const body = card; setCard(EMPTY);
    try { const { data } = await api.post("/admin/integrations/gateway/live-test/charge", body); toast[data.outcome === "PASS" ? "success" : "error"](`Live test: ${data.status} (${data.outcome})`); }
    catch (err) { toast.error(errMsg(err)); } finally { inflight.current = false; setBusy(false); load(); }
  };
  return (
    <div className="space-y-2 border-t pt-4" data-testid="gateway-live-test">
      <h3 className="font-semibold text-navy">Live $1.00 test payment</h3>
      <p className="text-xs text-slate-600">REAL charge, Super Admin only. Public payments stay on the simulated adapter. Attempts {s.attempts}/{s.max_attempts}.</p>
      {!s.enabled && <p className="text-xs font-semibold text-amber-900" data-testid="live-test-disabled">Disabled: {s.disabled_reason}.</p>}
      {s.enabled && <button type="button" className="btn-outline" disabled={busy} onClick={pre} data-testid="live-test-precheck-btn">{busy ? "Working…" : "Run no-card pre-check"}</button>}
      <Precheck p={s.precheck} />
      {s.ready && (
        <form onSubmit={charge} className="grid max-w-xl grid-cols-2 gap-2" data-testid="live-test-form" autoComplete="on">
          {FIELDS.map(([k, l, ac]) => (
            <label key={k} className="text-xs">{l}{["name", "number", "exp", "cvv"].includes(k) && " *"}
              <input className="input mt-0.5 w-full" value={card[k]} autoComplete={ac} type={k === "cvv" ? "password" : "text"} inputMode={["number", "cvv"].includes(k) ? "numeric" : undefined}
                required={["name", "number", "exp", "cvv"].includes(k)} onChange={(e) => setCard({ ...card, [k]: e.target.value })} data-testid={`live-test-${k}`} /></label>))}
          <p className="col-span-2 text-sm">Amount: <b data-testid="live-test-amount">1.00 {s.precheck.currency.toUpperCase()}</b> (fixed)</p>
          <button className="btn-primary col-span-2" disabled={busy} data-testid="live-test-charge-btn">{busy ? "Charging…" : "Charge $1.00 now"}</button>
        </form>)}
      <Result r={s.last} onChange={load} />
    </div>
  );
}
